import { COORDINATES_DENMARK } from '~/shared/constants';
import {
    haversineDistance,
    prioritizeStoresByPinnedIds,
    trimCityArea,
    type Coordinates,
} from '~/shared/utils';
import type { Store, StoreProps, StoreSortingType } from '~/lib/data-contract/salesforce';
import type { SalesforceProduct } from '~/lib/salesforce/types';
import { canDeliverFromStore, canReserveFromStore } from './utils/reservationStoreStatus';
import { isValidDanishPostalCode } from '~/shared/hooks/useCitySearch';
import type { StoreLocation } from '~/shared/hooks/useStoreLocationSearch';

type ResolveStoreParamsInput = {
    pid?: string;
    isStorePersonnel?: boolean;
    isPlusMember?: boolean;
    postalCode?: string;
};

/**
 * Resolves the SFCC store query params for the current customer.
 *
 * - Store personnel / Plus members with a postal code get distance sorting (`instore`/`rmembers`).
 * - Everyone else — guests, or logged-in customers without a postal code — falls back to the
 *   `anonymous` city/weight ordering (the Denmark coordinates only satisfy the query's `enabled`
 *   guard; SFCC ignores them for that sort).
 */
export const resolveStoreParams = ({
    pid,
    isStorePersonnel,
    isPlusMember,
    postalCode,
}: ResolveStoreParamsInput): StoreProps => {
    const base: StoreProps = { pid, count: 200, maxDistance: 10000 };

    if ((isStorePersonnel || isPlusMember) && postalCode) {
        const type: StoreSortingType = isStorePersonnel ? 'instore' : 'rmembers';
        return { ...base, type, postalCode };
    }

    return { ...base, type: 'anonymous', ...COORDINATES_DENMARK };
};

type ComposeDefaultStoresInput = {
    stores?: Store[];
    type?: StoreSortingType;
    prevCRStoreId?: string;
    prevCCStoreId?: string;
};

/**
 * Composes the resting-view store list. For guests (`anonymous`) and Plus members (`rmembers`) the
 * previously-selected C&R and C&C stores are looked up by id and prepended (C&R first, then C&C),
 * deduped so a store shows once at the top; unresolved ids are skipped. Store personnel
 * (`instore`) get the server order untouched, since their distance sorting already surfaces the
 * store they work at. The  pinned-store prioritization is always applied last.
 */
export const composeDefaultStores = ({
    stores,
    type,
    prevCRStoreId,
    prevCCStoreId,
}: ComposeDefaultStoresInput): Store[] | undefined => {
    if (!stores) return undefined;

    const supportsPrepend = type === 'anonymous' || type === 'rmembers';

    if (!supportsPrepend || (!prevCRStoreId && !prevCCStoreId)) {
        return prioritizeStoresByPinnedIds(stores);
    }

    const prepended: Store[] = [];
    const prependedIds = new Set<string>();

    [prevCRStoreId, prevCCStoreId].forEach((id) => {
        if (!id || prependedIds.has(id)) return;
        const match = stores.find((store) => store.id === id);
        if (match) {
            prepended.push(match);
            prependedIds.add(id);
        }
    });

    if (!prepended.length) {
        return prioritizeStoresByPinnedIds(stores);
    }

    const rest = stores.filter((store) => !prependedIds.has(store.id));
    return prioritizeStoresByPinnedIds([...prepended, ...rest]);
};

// Zip searches group by exact postal code. Otherwise by city with the area suffix trimmed.
type StoreGroupBy = 'city' | 'postalCode';

const normalizeCity = (city: string = '') =>
    city.trim().replace(/\s+/g, ' ').toLowerCase().replace(/å/g, 'aa');

// One key for stores and search input, so "København S" matches the København group.
const toCityOrPostalCodeKey = (value: string, groupBy: StoreGroupBy) =>
    groupBy === 'postalCode' ? value.trim() : trimCityArea(normalizeCity(value));

const getStoreCityOrPostalCodeKey = (store: Store, groupBy: StoreGroupBy) =>
    toCityOrPostalCodeKey(groupBy === 'postalCode' ? store.postalCode ?? '' : store.city, groupBy);

// 0 = can reserve, 1 = can click and collect, 2 = neither.
type EligibilityTier = 0 | 1 | 2;

// Checks quantity 1 so stock level does not affect order.
const getEligibilityTier = (store: Store, product?: SalesforceProduct | null): EligibilityTier => {
    if (canReserveFromStore(store)) return 0;
    if (canDeliverFromStore(store, product)) return 1;
    return 2;
};

type RankedStore = {
    store: Store;
    groupKey: string;
    distance: number;
    tier: EligibilityTier;
    // 0 when the store city matches the search exactly, fx "København S".
    cityMatchRank: 0 | 1;
};

const hasCoordinates = (store: Store): store is Store & Coordinates =>
    typeof store.latitude === 'number' && typeof store.longitude === 'number';

// Groups (city or zip) by nearest store, then exact city match, then C&R, C&C, neither. Pinned stores apply last.
export const sortStoresByLocation = (
    stores: Store[],
    { coordinates, query }: StoreLocation,
    product?: SalesforceProduct | null,
): Store[] => {
    const groupBy: StoreGroupBy = query && isValidDanishPostalCode(query) ? 'postalCode' : 'city';
    const searchKey = query ? toCityOrPostalCodeKey(query, groupBy) : undefined;
    const searchedCity = query ? normalizeCity(query) : undefined;

    const ranked = stores.filter(hasCoordinates).map(
        (store): RankedStore => ({
            store,
            groupKey: getStoreCityOrPostalCodeKey(store, groupBy),
            distance: haversineDistance(coordinates, store),
            tier: getEligibilityTier(store, product),
            cityMatchRank: normalizeCity(store.city) === searchedCity ? 0 : 1,
        }),
    );

    // The searched group goes first. Other groups sit at their nearest store.
    const groupDistance = new Map<string, number>();
    ranked.forEach(({ groupKey, distance }) => {
        const value = groupKey === searchKey ? -1 : distance;
        groupDistance.set(groupKey, Math.min(value, groupDistance.get(groupKey) ?? Infinity));
    });

    const sorted = ranked
        .map((entry) => ({
            ...entry,
            groupDistance: groupDistance.get(entry.groupKey) ?? Infinity,
        }))
        .sort(
            (a, b) =>
                a.groupDistance - b.groupDistance ||
                a.groupKey.localeCompare(b.groupKey) ||
                a.cityMatchRank - b.cityMatchRank ||
                a.tier - b.tier ||
                (a.store.city ?? '').localeCompare(b.store.city ?? '', 'da') ||
                a.store.c_weight - b.store.c_weight ||
                a.distance - b.distance,
        )
        .map((entry) => entry.store);

    return prioritizeStoresByPinnedIds(sorted);
};
